class AddressDrawer extends DrawerComponent {
    get requiresBodyAppended() {
        return false;
    }

    constructor() {
        super();
        this.refCache = {};
        this.addressFormBound = false;
        this.autoSubmittedFromCookie = false;
        this.handleFormSubmit = this.handleFormSubmit.bind(this);

        this.events = {
            handleAfterHide: 'drawer:handleAfterHide',
            handleAfterShow: 'drawer:handleAfterShow',
        };

        this.pendingQuery = null;
        this.pendingGeocodeResult = null;
        this._submitSeq = 0;

        // Cache configuration
        this.CACHE_KEY_PREFIX = 'gmaps_geocode_';
        this.CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

        this._onStatusUpdated = this.handleDeliveryAddressStatusUpdated.bind(this);
        this._onConfirmClick = this.handleConfirmAddress.bind(this);

        // Closing the drawer abandons a geocode still in flight. Without this it lands after Cancel, becomes
        // the pending address, and the next "available" broadcast auto-confirms an address the buyer walked
        // away from (FPS-809). A result already pending is shut out by delivery-stock-status.js instead.
        this.addEventListener('toggle', (event) => {
            if (event.target !== this || this.open) return;
            this._submitSeq++;
            this.setSubmitLoading(false);
        });
    }

    get cacheDuration() {
        const duration = Number(this.dataset.cacheDuration);
        return Number.isFinite(duration) && duration > 0 ? duration : this.CACHE_DURATION;
    }

    get googleMapsApiKey() {
        return this.dataset.googleMapsApiKey || '';
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('deliveryAddress:statusUpdated', this._onStatusUpdated);
        this.initAddressForm();
        this.restoreAddressQueryFromCookie({ autoSubmit: true });

        // Clear expired cache entries on initialization (async, non-blocking)
        Promise.resolve().then(() => this.clearExpiredCache());
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        document.removeEventListener('deliveryAddress:statusUpdated', this._onStatusUpdated);

        if (this.addressFormBound && this.addressForm) {
            this.addressForm.removeEventListener('submit', this.handleFormSubmit);
        }
        if (this._confirmButton) {
            this._confirmButton.removeEventListener('click', this._onConfirmClick);
            this._confirmButton = null;
        }

        this.addressFormBound = false;
        this.addressForm = null;
        this.refCache = {};
    }

    refs(value, scope = this) {
        if (this.refCache[value]) return this.refCache[value];
        this.refCache[value] = scope.querySelector(`[ref='${value}']`);
        return this.refCache[value];
    }

    initAddressForm() {
        if (this.addressFormBound) return;
        const form = this.querySelector('.address-form');
        const input = this.refs('addressInput');
        const confirmButton = this.refs('confirmButton');

        if (!form || !input) return;

        form.addEventListener('submit', this.handleFormSubmit);
        this.addressForm = form;
        this.addressFormBound = true;

        this._confirmButton = confirmButton;
        confirmButton.addEventListener('click', this._onConfirmClick);
    }

    handleDeliveryAddressStatusUpdated(event) {
        const { available = false } = event.detail;
        this.toggleConfirmButton(available);

        // Auto-confirm when the address is valid and in-zone — no need for the
        // user to press Confirm manually after submitting the form.
        if (available && this.pendingQuery && this.pendingGeocodeResult) {
            this.handleConfirmAddress();
        }
    }

    toggleConfirmButton(enabled = false) {
        const confirmButton = this.refs('confirmButton');
        if (!confirmButton) return;
        confirmButton.toggleAttribute('disabled', !enabled);
    }

    get cookieMaxAge() {
        const maxAge = Number(this.dataset.cookieMaxAge);
        return Number.isNaN(maxAge) ? null : maxAge;
    }

    setAddressQueryCookie(value) {
        try {
            const query = String(value || '').trim();
            if (!query) return;

            const encoded = encodeURIComponent(query);
            const maxAge = this.cookieMaxAge;
            const maxAgePart = Number.isFinite(maxAge) && maxAge > 0 ? `; Max-Age=${maxAge}` : '';

            document.cookie = `addressDrawerQuery=${encoded}; path=/; SameSite=Lax${maxAgePart}`;
        } catch (error) {
            console.error(error);
        }
    }

    getAddressQueryFromCookie() {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const prefix = 'addressDrawerQuery=';
        const cookie = cookies.find((entry) => entry.startsWith(prefix));
        if (!cookie) return null;

        const value = cookie.substring(prefix.length);
        if (!value) return null;

        try {
            return decodeURIComponent(value);
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    /**
     * Save geocoding result to cookie for later use
     * @param {Object} result - The complete geocoding result
     */
    setAddressResultCookie(result) {
        try {
            if (!result) return;

            const encoded = encodeURIComponent(JSON.stringify(result));
            const maxAge = this.cookieMaxAge;
            const maxAgePart = Number.isFinite(maxAge) && maxAge > 0 ? `; Max-Age=${maxAge}` : '';

            document.cookie = `addressDrawerResult=${encoded}; path=/; SameSite=Lax${maxAgePart}`;
        } catch (error) {
            console.error('[AddressDrawer] Error saving result cookie:', error);
        }
    }

    /**
     * Get saved geocoding result from cookie
     * @returns {Object|null} The geocoding result or null
     */
    getAddressResultFromCookie() {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const prefix = 'addressDrawerResult=';
        const cookie = cookies.find((entry) => entry.startsWith(prefix));
        if (!cookie) return null;

        const value = cookie.substring(prefix.length);
        if (!value) return null;

        try {
            return JSON.parse(decodeURIComponent(value));
        } catch (error) {
            console.error('[AddressDrawer] Error parsing result cookie:', error);
            return null;
        }
    }

    restoreAddressQueryFromCookie({ autoSubmit = false } = {}) {
        const input = this.refs('addressInput');
        if (!input) return;

        const saved = this.getAddressQueryFromCookie();
        if (!saved) return;

        // Only backfill when the user hasn't typed yet.
        if (!String(input.value || '').trim()) {
            input.value = saved;

            if (autoSubmit && !this.autoSubmittedFromCookie) {
                this.autoSubmittedFromCookie = true;
                Promise.resolve().then(() => this.submitAddressQuery(saved));
            }
        }
    }

    submitAddressQuery(query) {
        const input = this.refs('addressInput');
        if (!input) return;

        const value = String(query || '').trim();
        if (!value) return;

        input.value = value;
        // Reuse existing submit logic.
        return this.handleFormSubmit({
            preventDefault() { },
        });
    }

    setErrorVisible(visible) {
        const error = this.refs('errorMessage');
        if (!error) return;
        error.classList.toggle('hidden', !visible);
    }

    setSubmitLoading(isLoading) {
        const button = this.refs('submitAddress');
        if (!button) return;
        button.toggleAttribute('disabled', !!isLoading);
    }

    async handleFormSubmit(event) {
        event.preventDefault();
        const input = this.refs('addressInput');
        const query = input?.value?.trim();
        if (!query) return;

        // Hide error message before submitting
        this.setErrorVisible(false);
        this.setSubmitLoading(true);

        // connectedCallback() auto-resubmits the last saved query on every page load. If the
        // user submits a new address while that background geocode is still in flight, the two
        // requests race -- whichever resolves LAST wins, not whichever was SENT last, so a slow
        // stale response can overwrite the user's fresh one. Guard against that by only
        // committing this submission's result if it's still the most recent one requested.
        const submitToken = ++this._submitSeq;

        try {
            const geocodeResult = await this.geocodeAddressGoogle(query);
            if (submitToken !== this._submitSeq) return;
            if (!geocodeResult) {
                this.setErrorVisible(true);
                return;
            }

            // Store pending data until user confirms
            this.pendingQuery = query;
            this.pendingGeocodeResult = geocodeResult;

            // Dispatch event with complete geocoding result
            this.dispatchDeliveryAddressUpdated(geocodeResult);

        } catch (error) {
            if (submitToken !== this._submitSeq) return;
            console.error('[AddressDrawer] Error geocoding address:', error);
            this.setErrorVisible(true);
        } finally {
            if (submitToken === this._submitSeq) this.setSubmitLoading(false);
        }
    }

    /**
     * Dispatch deliveryAddress:updated event with geocoding result
     * @param {Object} geocodeResult - The complete geocoding result
     */
    dispatchDeliveryAddressUpdated(geocodeResult) {
        const event = new CustomEvent('deliveryAddress:updated', {
            detail: {
                latitude: geocodeResult.latitude,
                longitude: geocodeResult.longitude,
                formatted_address: geocodeResult.formatted_address,
                address_components: geocodeResult.address_components,
                place_id: geocodeResult.place_id,
                _raw: geocodeResult._raw
            },
            bubbles: true,
            composed: true
        });

        document.dispatchEvent(event);
    }



    /**
     * Generate cache key from address query
     * @param {string} address - The address query
     * @returns {string} Cache key
     */
    getCacheKey(address) {
        const normalized = String(address || '').trim().toLowerCase();
        return `${this.CACHE_KEY_PREFIX}${encodeURIComponent(normalized)}`;
    }

    /**
     * Get cached geocoding result
     * @param {string} address - The address query
     * @returns {Object|null} Cached result or null if not found/expired
     */
    getCachedResult(address) {
        try {
            const cacheKey = this.getCacheKey(address);
            const cached = localStorage.getItem(cacheKey);

            if (!cached) return null;

            const data = JSON.parse(cached);
            const now = Date.now();

            // Check if cache is expired
            if (data.expiresAt && data.expiresAt < now) {
                localStorage.removeItem(cacheKey);
                return null;
            }

            return data.result;
        } catch (error) {
            console.error('[AddressDrawer] Error reading cache:', error);
            return null;
        }
    }

    /**
     * Save geocoding result to cache
     * @param {string} address - The address query
     * @param {Object} result - The geocoding result
     */
    setCachedResult(address, result) {
        try {
            const cacheKey = this.getCacheKey(address);
            const expiresAt = Date.now() + this.cacheDuration;

            const data = {
                result,
                expiresAt,
                cachedAt: Date.now(),
                query: address
            };

            localStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (error) {
            console.error('[AddressDrawer] Error saving cache:', error);
            // If localStorage is full, try to clear old entries
            if (error.name === 'QuotaExceededError') {
                this.clearExpiredCache();
            }
        }
    }

    /**
     * Clear expired cache entries
     */
    clearExpiredCache() {
        try {
            const now = Date.now();
            let clearedCount = 0;

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key || !key.startsWith(this.CACHE_KEY_PREFIX)) continue;

                try {
                    const data = JSON.parse(localStorage.getItem(key));
                    if (data.expiresAt && data.expiresAt < now) {
                        localStorage.removeItem(key);
                        clearedCount++;
                    }
                } catch (e) {
                    // Remove corrupted entries
                    localStorage.removeItem(key);
                    clearedCount++;
                }
            }

        } catch (error) {
            console.error('[AddressDrawer] Error clearing cache:', error);
        }
    }



    /**
     * Geocode address using Google Maps JavaScript SDK with caching
     * @param {string} address - The address to geocode
     * @returns {Promise<Object|null>} Geocode result with latitude, longitude, and formatted_address
     */
    async geocodeAddressGoogle(address) {
        // Check cache first
        const cached = this.getCachedResult(address);
        if (cached) return cached;

        const apiKey = this.googleMapsApiKey;
        if (!apiKey) {
            console.error('[AddressDrawer] Google Maps API key not provided');
            return null;
        }

        try {
            const geocodeResult = await window.GoogleMapsGeocoder.geocodeAddress(address, apiKey);

            if (!geocodeResult) {
                console.warn('[AddressDrawer] No geocoding results found for address:', address);
                return null;
            }

            // Cache the result
            this.setCachedResult(address, geocodeResult);

            return geocodeResult;
        } catch (error) {
            console.error('[AddressDrawer] Error geocoding address:', error);
            return null;
        }
    }

    /**
     * Parse Google Maps address_components into a more usable format
     * @param {Array} components - Array of address components from Google Maps API
     * @returns {Object} Parsed address components
     */
    parseAddressComponents(components) {
        if (!Array.isArray(components)) return {};

        const parsed = {
            street_number: '',
            route: '',
            locality: '',
            administrative_area_level_1: '',
            administrative_area_level_2: '',
            country: '',
            postal_code: '',
        };

        components.forEach(component => {
            const type = component.types[0];
            if (parsed.hasOwnProperty(type)) {
                parsed[type] = component.long_name || component.short_name;
            }
        });

        return parsed;
    }

    handleConfirmAddress() {
        if (!this.pendingQuery || !this.pendingGeocodeResult) return;

        this.setAddressQueryCookie(this.pendingQuery);
        this.setAddressResultCookie(this.pendingGeocodeResult);

        // Fire after the cookie is written so listeners can safely read it.
        document.dispatchEvent(new CustomEvent('deliveryAddress:confirmed', {
            detail: { result: this.pendingGeocodeResult },
        }));

        this.pendingQuery = null;
        this.pendingGeocodeResult = null;

        this.hide();
    }

    /**
     * Static helper to get saved address result from anywhere in the app
     * @returns {Object|null} The saved geocoding result
     */
    static getSavedAddressResult() {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const prefix = 'addressDrawerResult=';
        const cookie = cookies.find((entry) => entry.startsWith(prefix));
        if (!cookie) return null;

        const value = cookie.substring(prefix.length);
        if (!value) return null;

        try {
            return JSON.parse(decodeURIComponent(value));
        } catch (error) {
            console.error('[AddressDrawer] Error parsing saved result:', error);
            return null;
        }
    }
}
customElements.define('address-drawer', AddressDrawer);
